import { createTheme, ThemeOptions } from "@mui/material/styles";

// Light theme configuration
const lightThemeOptions: ThemeOptions = {
  palette: {
    mode: "light",
    primary: {
      main: "#0d6efd",
      light: "#3d8bfd",
      dark: "#0b5ed7",
      contrastText: "#fff",
    },
    secondary: {
      main: "#6c757d",
      light: "#adb5bd",
      dark: "#495057",
    },
    error: {
      main: "#dc3545",
      light: "#e4606d",
      dark: "#c82333",
    },
    warning: {
      main: "#ffc107",
      light: "#ffcd38",
      dark: "#e0a800",
    },
    info: {
      main: "#0dcaf0",
      light: "#3dd5f3",
      dark: "#0aa2c0",
    },
    success: {
      main: "#198754",
      light: "#47a76a",
      dark: "#146c43",
    },
    background: {
      default: "#f8f9fa",
      paper: "#ffffff",
    },
    text: {
      primary: "#212529",
      secondary: "#6c757d",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: "3rem",
      fontWeight: 700,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: "2.5rem",
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: "2rem",
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: "1.5rem",
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: "1.25rem",
      fontWeight: 500,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: "1rem",
      fontWeight: 500,
      lineHeight: 1.5,
    },
    button: {
      textTransform: "none",
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "50px",
          padding: "10px 24px",
          fontSize: "1rem",
          boxShadow: "none",
          "&:hover": {
            boxShadow: "0 4px 15px rgba(0, 0, 0, 0.2)",
          },
        },
        sizeLarge: {
          padding: "12px 32px",
          fontSize: "1.1rem",
        },
        sizeSmall: {
          padding: "6px 16px",
          fontSize: "0.875rem",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          "&:hover": {
            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15)",
          },
        },
      },
    },
  },
};

// Dark theme configuration
const darkThemeOptions: ThemeOptions = {
  palette: {
    mode: "dark",
    primary: {
      main: "#0d6efd",
      light: "#3d8bfd",
      dark: "#0b5ed7",
      contrastText: "#fff",
    },
    secondary: {
      main: "#adb5bd",
      light: "#ced4da",
      dark: "#6c757d",
    },
    error: {
      main: "#dc3545",
      light: "#e4606d",
      dark: "#c82333",
    },
    warning: {
      main: "#ffc107",
      light: "#ffcd38",
      dark: "#e0a800",
    },
    info: {
      main: "#0dcaf0",
      light: "#3dd5f3",
      dark: "#0aa2c0",
    },
    success: {
      main: "#198754",
      light: "#47a76a",
      dark: "#146c43",
    },
    background: {
      default: "#121212",
      paper: "#1e1e1e",
    },
    text: {
      primary: "#f1f1f1",
      secondary: "#b0b0b0",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: "3rem",
      fontWeight: 700,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: "2.5rem",
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: "2rem",
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: "1.5rem",
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: "1.25rem",
      fontWeight: 500,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: "1rem",
      fontWeight: 500,
      lineHeight: 1.5,
    },
    button: {
      textTransform: "none",
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "50px",
          padding: "10px 24px",
          fontSize: "1rem",
          boxShadow: "none",
          "&:hover": {
            boxShadow: "0 4px 15px rgba(255, 255, 255, 0.1)",
          },
        },
        sizeLarge: {
          padding: "12px 32px",
          fontSize: "1.1rem",
        },
        sizeSmall: {
          padding: "6px 16px",
          fontSize: "0.875rem",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: "0 2px 8px rgba(255, 255, 255, 0.05)",
          "&:hover": {
            boxShadow: "0 4px 16px rgba(255, 255, 255, 0.1)",
          },
        },
      },
    },
  },
};

export const lightTheme = createTheme(lightThemeOptions);
export const darkTheme = createTheme(darkThemeOptions);
